import { useCallback, useContext } from 'react';
import { CopilotContext } from '@mindtickle/ui-shell-core/Copilot';
import {
  CopilotContextData,
  ConversationHistory,
  CopilotScope,
} from '@mindtickle/ui-shell-core/Copilot';

/**
 * Hook for using Copilot context in components
 *
 * Provides simplified access to Copilot context functions with domain and feature specific operations
 *
 * @param scope - The scope containing domain and feature for the copilot context
 */
const useCopilot = (scope: CopilotScope) => {
  const context = useContext(CopilotContext);

  const getData = useCallback(() => context?.getDomainData(scope), [context, scope]);

  const setData = useCallback((data: CopilotContextData) => context?.setDomainData(scope, data), [
    context,
    scope,
  ]);

  const getKey = useCallback(
    <T,>(key: keyof CopilotContextData): T | undefined => context?.getKey<T>(scope, key),
    [context, scope]
  );

  const setKey = useCallback(
    <T,>(key: keyof CopilotContextData, value: T) => context?.setKey<T>(scope, key, value),
    [context, scope]
  );

  const getDomain = useCallback((): string => scope.domain, [scope]);

  const getFeature = useCallback((): string => scope.feature, [scope]);
  const getSessionId = useCallback((): string | undefined => getKey<string>('sessionId'), [getKey]);

  const getSessionMode = useCallback((): string | undefined => getKey<string>('sessionMode'), [
    getKey,
  ]);

  const getConversation = useCallback(
    (): ConversationHistory | undefined =>
      getKey<ConversationHistory>('conversation' as keyof CopilotContextData),
    [getKey]
  );

  const getQuery = useCallback((): string | undefined => getKey<string>('query'), [getKey]);

  const getAutoExecute = useCallback((): boolean | undefined => getKey<boolean>('autoExecute'), [
    getKey,
  ]);

  const getLocationId = useCallback((): string | undefined => getKey<string>('locationId'), [
    getKey,
  ]);

  const getChannelType = useCallback(
    (): string | undefined => getKey<string>('channelType' as keyof CopilotContextData),
    [getKey]
  );

  const getOpenCopilot = useCallback(
    (): boolean | undefined => getKey<boolean>('openCopilot' as keyof CopilotContextData),
    [getKey]
  );

  // Field-specific setters
  const setSessionId = useCallback((value: string) => setKey('sessionId', value), [setKey]);

  const setSessionMode = useCallback((value: string) => setKey('sessionMode', value), [setKey]);

  const setConversation = useCallback(
    (value: ConversationHistory) => setKey('conversation' as keyof CopilotContextData, value),
    [setKey]
  );

  const setQuery = useCallback((value: string) => setKey('query', value), [setKey]);

  const setAutoExecute = useCallback((value: boolean) => setKey('autoExecute', value), [setKey]);

  const setLocationId = useCallback((value: string | undefined) => setKey('locationId', value), [
    setKey,
  ]);

  // Set channelType value
  const setChannelType = useCallback(
    (value: string) => setKey('channelType' as keyof CopilotContextData, value),
    [setKey]
  );

  // Set openCopilot value
  const setOpenCopilot = useCallback(
    (value: boolean) => setKey('openCopilot' as keyof CopilotContextData, value),
    [setKey]
  );

  // Get ref value
  const getRef = useCallback(() => context?.getRef(scope), [context, scope]);

  // Set ref value
  const setRef = useCallback((value: any) => context?.setRef(scope, value), [context, scope]);

  // Trigger Copilot functionality - either send message via ref or open the Copilot
  const triggerCopilot = useCallback(
    (data: CopilotContextData) => {
      const ref = getRef();
      setData({
        ...data,
        openCopilot: true,
      });
      if (ref?.triggerCopilot) {
        // If we have a valid ref with the method, call it with the query
        ref.triggerCopilot(data);
      }
    },
    [getRef, setData, setOpenCopilot]
  );

  return {
    // Generic methods
    getData,
    setData,

    // Domain and feature getters/setters
    getDomain,
    getFeature,

    // Field-specific getters
    getSessionId,
    getSessionMode,
    getConversation,
    getQuery,
    getAutoExecute,
    getRef,
    getLocationId,
    getChannelType,
    getOpenCopilot,

    // Field-specific setters
    setSessionId,
    setSessionMode,
    setConversation,
    setQuery,
    setAutoExecute,
    setRef,
    setLocationId,
    setChannelType,
    setOpenCopilot,

    // Action methods
    triggerCopilot,
    domainMap: context?.domainMap,
  };
};

export default useCopilot;
